<template>
  <div :class="`lvl${level}-category-container`">
    <SliderModalItem v-if="parentCategory !== null">
      <UiRadioButton
        :id="parentCategory['id']"
        v-model="store.selectedCategory"
        class="body-m"
        :input-value="parentCategory['slugUrl']"
        :label="
          $t('products.filters.selectAll', {
            category: parentCategory['label'],
          })
        "
        :label-position="LabelPositionOpts.LEFT"
        @update:selected-option="setSelectedCategory($event.toString())"
      />
    </SliderModalItem>

    <SliderModalItem v-for="category in categoryTree" :key="category['id']">
      <SliderModalContainer
        v-if="category['children'].length"
        :modal-title="category['label']"
        modal-header-animation="teleport"
        modal-close-btn-icon="chevron"
        :modal-clear-btn-text="
          isSelectedCatRelatedTo(category['slugUrl'])
            ? $t('products.filters.clear')
            : ''
        "
        @clear="setSelectedCategory('')"
      >
        <template #slider-modal-trigger-btn>
          <SliderModalNavBtn
            :btn-title="category['label']"
            :btn-text="
              isSelectedCatRelatedTo(category['slugUrl'])
                ? formattedSelection
                : ''
            "
          />
        </template>

        <template #slider-modal-content>
          <CategoryTree
            :parent-category="removeChildren(category)"
            :category-tree="category['children']"
            :formatted-selection="formattedSelection"
            :level="level + 1"
          />
        </template>
      </SliderModalContainer>

      <UiRadioButton
        v-else
        :id="category['id']"
        v-model="store.selectedCategory"
        class="body-m"
        :input-value="category['slugUrl']"
        :label="category['label']"
        :label-position="LabelPositionOpts.LEFT"
        :is-force-checked="isAllRadioSelected"
        @click="isAllRadioSelected = false"
        @update:selected-option="setSelectedCategory($event.toString())"
      />
    </SliderModalItem>
  </div>
</template>

<script lang="ts" setup>
import { type CategoryFilters } from '~/types/api/data/product/filter/filter.types'
import SliderModalItem from './slider-modal-item.vue'
import SliderModalContainer from './slider-modal-container.vue'
import SliderModalNavBtn from './slider-modal-nav-btn.vue'
import UiRadioButton, { LabelPositionOpts } from '@sephora-asia/ui-radio-button'

interface CategoryTree extends CategoryFilters {
  children: CategoryTree[]
}

const props = withDefaults(
  defineProps<{
    categoryTree: CategoryTree[]
    parentCategory?: CategoryFilters | null
    formattedSelection?: string
    level?: number
  }>(),
  {
    parentCategory: null,
    formattedSelection: undefined,
    level: 1,
  }
)

const store = useProductListingFiltersStore() // Desctructuring from store breaks top-level refs
const { setSelectedCategory } = store

/**
 * ----- Local States -----
 */
const isAllRadioSelected = ref<boolean>(
  store.selectedCategory === props.parentCategory?.slugUrl
)

/**
 * ----- Watchers -----
 */
watch(
  () => store.selectedCategory,
  () => {
    isAllRadioSelected.value =
      store.selectedCategory === props.parentCategory?.slugUrl
  }
)

/**
 * ----- Methods -----
 */

function removeChildren(category: CategoryTree) {
  return {
    id: category['id'],
    label: category['label'],
    slugUrl: category['slugUrl'],
  }
}

function isSelectedCatRelatedTo(categorySlug: string) {
  const selectedCat = store.selectedCategory.split('/')
  const displayedCat = categorySlug.split('/')

  // Loop matching because 'skincare/cleanser' can include 'clean'
  for (const [index, cat] of displayedCat.entries()) {
    if (selectedCat[index] !== cat) {
      return false
    }
  }
  return true
}
</script>

<style lang="scss" scoped></style>
